import { useState } from 'react'
import { Stack, Text, Box, Group, Badge, Accordion, Table, Skeleton, Alert, Progress } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { useCurrentTenant } from '@/store/auth'
import { securityApi } from '@/api/security'
import { WidgetCard } from '@/components/ui'
import { ScoreGauge } from '@/components/security/ScoreGauge'
import { SeverityBadge } from '@/components/security/SeverityBadge'
import { FindingDrawer } from '@/components/security/FindingDrawer'
import type { SecurityFinding } from '@/api/security'

const CATEGORY_DESCRIPTIONS: Record<string, { name: string; description: string }> = {
  A01: { name: 'Broken Access Control', description: 'Restrictions on what authenticated users are allowed to do are often not properly enforced. Attackers can exploit these flaws to access unauthorized functionality and/or data.' },
  A02: { name: 'Cryptographic Failures', description: 'Failures related to cryptography which often leads to sensitive data exposure. Includes weak encryption, missing encryption, or improper key management.' },
  A03: { name: 'Injection', description: 'Injection flaws occur when untrusted data is sent to an interpreter as part of a command or query. Hostile data can trick the interpreter into executing unintended commands or accessing data without proper authorization.' },
  A04: { name: 'Insecure Design', description: 'Risks related to design and architectural flaws, with a call for more use of threat modeling, secure design patterns, and reference architectures.' },
  A05: { name: 'Security Misconfiguration', description: 'The most commonly seen issue. This includes insecure default configurations, incomplete configurations, open cloud storage, misconfigured HTTP headers, and verbose error messages containing sensitive information.' },
  A06: { name: 'Vulnerable and Outdated Components', description: 'Components such as libraries, frameworks, and software modules run with the same privileges as the application. If a vulnerable component is exploited, it can facilitate serious data loss or server takeover.' },
  A07: { name: 'Identification and Authentication Failures', description: 'Confirmation of the identity, authentication, and session management is critical. These failures can allow attackers to compromise passwords, keys, or session tokens.' },
  A08: { name: 'Software and Data Integrity Failures', description: 'Failures relating to code and infrastructure that does not protect against integrity violations. Includes insecure CI/CD pipelines and auto-update mechanisms.' },
  A09: { name: 'Security Logging and Monitoring Failures', description: 'Insufficient logging, detection, monitoring, and active response allows attackers to further attack systems, maintain persistence, pivot to more systems, and tamper, extract, or destroy data.' },
  A10: { name: 'Server-Side Request Forgery (SSRF)', description: 'SSRF flaws occur whenever a web application fetches a remote resource without validating the user-supplied URL. It allows an attacker to coerce the application to send a crafted request to an unexpected destination.' },
}

export function CategoryDetail() {
  const { category } = useParams<{ category: string }>()
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  const [selectedFinding, setSelectedFinding] = useState<SecurityFinding | null>(null)
  const [drawerOpened, setDrawerOpened] = useState(false)

  const catInfo = category ? CATEGORY_DESCRIPTIONS[category] : undefined

  const overview = useQuery({
    queryKey: ['security', 'overview', tenantId],
    queryFn: () => securityApi.getOverview(tenantId!),
    enabled: !!tenantId,
  })

  const findings = useQuery({
    queryKey: ['security', 'findings', tenantId, 'category', category],
    queryFn: () => securityApi.listFindings(tenantId!, { category }),
    enabled: !!tenantId && !!category,
  })

  if (!tenantId) return <Alert color="yellow">Selecione um tenant para visualizar os detalhes da categoria.</Alert>
  if (!category || !catInfo) return <Alert color="red">Categoria OWASP inválida.</Alert>

  const latestScan = overview.data?.latest_scans?.[0]
  const categoryScore = latestScan?.scores_by_category?.[category] ?? 0
  const findingsList = findings.data?.findings ?? []

  // Group findings by check_id for accordion
  const findingsByCheck = findingsList.reduce<Record<string, SecurityFinding[]>>((acc, f) => {
    if (!acc[f.check_id]) acc[f.check_id] = []
    acc[f.check_id].push(f)
    return acc
  }, {})

  // Severity distribution for the bar
  const severityCounts: Record<string, number> = {}
  findingsList.forEach(f => {
    severityCounts[f.severity] = (severityCounts[f.severity] ?? 0) + 1
  })
  const total = findingsList.length || 1

  return (
    <Stack gap="xl">
      {/* Header */}
      <Group align="flex-start" gap="xl">
        <WidgetCard accent="primary" p="md" style={{ minWidth: 180 }}>
          {overview.isLoading ? (
            <Skeleton height={160} />
          ) : (
            <ScoreGauge score={categoryScore} label={`${category} Score`} />
          )}
        </WidgetCard>

        <Box style={{ flex: 1 }}>
          <Group gap="sm" mb="xs">
            <Badge size="lg" variant="filled" color="blue">{category}</Badge>
            <Text fw={700} size="xl">{catInfo.name}</Text>
          </Group>
          <Text size="sm" c="dimmed" mb="md">{catInfo.description}</Text>

          <Text size="sm" fw={600} mb="xs">Severity Distribution</Text>
          <Group gap={4} mb="xs">
            {['critical', 'high', 'medium', 'low', 'info'].map(sev => {
              const count = severityCounts[sev] ?? 0
              if (count === 0) return null
              const colorMap: Record<string, string> = { critical: 'red', high: 'orange', medium: 'yellow', low: 'blue', info: 'gray' }
              return (
                <Progress.Root key={sev} size="lg" style={{ flex: count }}>
                  <Progress.Section value={100} color={colorMap[sev]}>
                    <Progress.Label>{count}</Progress.Label>
                  </Progress.Section>
                </Progress.Root>
              )
            })}
          </Group>
          <Text size="xs" c="dimmed">{findingsList.length} findings total</Text>
        </Box>
      </Group>

      {/* Checks Accordion */}
      <WidgetCard>
        <Text size="sm" fw={600} mb="md">Checks ({Object.keys(findingsByCheck).length})</Text>
        {findings.isLoading ? (
          <Skeleton height={200} />
        ) : Object.keys(findingsByCheck).length > 0 ? (
          <Accordion variant="separated">
            {Object.entries(findingsByCheck).map(([checkId, checkFindings]) => {
              const severities = [...new Set(checkFindings.map(f => f.severity))]
              const highestSeverity = ['critical', 'high', 'medium', 'low', 'info'].find(
                s => severities.includes(s)
              ) ?? 'info'
              return (
                <Accordion.Item key={checkId} value={checkId}>
                  <Accordion.Control>
                    <Group gap="sm">
                      <SeverityBadge severity={highestSeverity} size="xs" />
                      <Text size="sm" fw={600}>{checkId}</Text>
                      <Text size="sm" c="dimmed">{checkFindings[0]?.title}</Text>
                      <Badge size="xs" variant="light" ml="auto">{checkFindings.length}</Badge>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Table striped withTableBorder>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Severity</Table.Th>
                          <Table.Th>Resource</Table.Th>
                          <Table.Th>Region</Table.Th>
                          <Table.Th>Status</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {checkFindings.map(f => (
                          <Table.Tr
                            key={f.id}
                            style={{ cursor: 'pointer' }}
                            onClick={() => { setSelectedFinding(f); setDrawerOpened(true) }}
                          >
                            <Table.Td><SeverityBadge severity={f.severity} size="xs" /></Table.Td>
                            <Table.Td><Text size="xs" ff="monospace" lineClamp={1}>{f.resource_arn || f.resource_type || 'N/A'}</Text></Table.Td>
                            <Table.Td><Text size="xs">{f.region || 'N/A'}</Text></Table.Td>
                            <Table.Td><Badge size="xs" variant="outline">{f.status}</Badge></Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Accordion.Panel>
                </Accordion.Item>
              )
            })}
          </Accordion>
        ) : (
          <Text size="sm" c="dimmed" ta="center" py="xl">Nenhum finding nesta categoria.</Text>
        )}
      </WidgetCard>

      <FindingDrawer
        finding={selectedFinding}
        opened={drawerOpened}
        onClose={() => { setDrawerOpened(false); setSelectedFinding(null) }}
      />
    </Stack>
  )
}
